import React, { useContext, useEffect, useState } from "react";
import AppContext from "../../context/Context";
import PageHeader from "../common/PageHeader";
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useSelector } from "react-redux";
import FalconCardHeader from "../common/FalconCardHeader";
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import { findOneConfig } from "../../helpers/api/condominium";
import ButtonIcon from "../common/ButtonIcon";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import { updateConfig } from "../../helpers/api/condominium";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import withRedirect from "../../helpers/hoc/withRedirect";
import { Toast } from "./../UI/toast/Toast";
import { PreloadState } from "../UI/preloading/PreloadState";
import OnboardingVisibilityCard from "../onboarding/OnboardingVisibilityCard";
import { faUndo } from "@fortawesome/free-solid-svg-icons";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import SupportLink from "../root/support/components/SupportLink";
import ImageCropper from "../common/ImageCropper";

const OWNER_EMAILS = [
  {
    key: "payment_report_received",
    label: "Pago reportado",
    help: "Le confirma al propietario que recibimos su reporte de pago.",
  },
  {
    key: "payment_approved",
    label: "Pago aprobado",
    help: "Cuando apruebas un pago que reportó el propietario.",
  },
  {
    key: "payment_registered",
    label: "Pago registrado por la administración",
    help: "Cuando registras un pago a nombre del propietario.",
  },
  {
    key: "reservation_confirmed",
    label: "Reserva confirmada",
    help: "Cuando se confirma una reserva de un área común.",
  },
  {
    key: "visitor_registered",
    label: "Visita registrada",
    help: "Cuando el propietario registra un visitante.",
  },
  {
    key: "release_published",
    label: "Comunicados",
    help: "Cada comunicado que se publica les llega por correo a todos los propietarios.",
  },
  {
    key: "charge_day_planilla",
    label: "Planilla en el día de cobro",
    help: "El día de cobro del condominio, con sus cuentas por pagar.",
    onlyWithChargeDay: true,
  },
];

const ADMIN_EMAILS = [
  {
    key: "admin_payment_reported",
    label: "Nuevo pago reportado",
    help: "Cuando un propietario reporta un pago.",
  },
  {
    key: "initial_load_reminder",
    label: "Recordatorio de carga inicial",
    help: "Los lunes, mientras falte completar la carga inicial.",
  },
  {
    key: "planilla_emission_reminder",
    label: "Recordatorio de emitir la planilla",
    help: "El día 1 de cada mes, mientras no hayas emitido tu primera planilla.",
  },
];

const Config = () => {
  const { selectedCondominium, condominium } = useSelector(
    (state) => state.condominium
  );
  const { isFluid } = useContext(AppContext);
  const history = useHistory();
  const { register, errors } = useForm();

  const [loading, setLoading] = useState(false);
  const [edited, setEdited] = useState(false);
  const { data, isLoading } = useSWRFetch(selectedCondominium, findOneConfig);
  // Siempre 1 o 0, nunca un booleano: los radios de abajo comparan con === 1
  // y === 0, y el valor se manda tal cual en un FormData.
  const [selectedOption, setSelectedOption] = useState(0);
  const [note, setNote] = useState("");
  const [logo, setLogo] = useState(null);
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [autoPayWithCreditBalance, setAutoPayWithCreditBalance] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState({});
  const [showControlNumber, setShowControlNumber] = useState(false);
  const [nextControlNumber, setNextControlNumber] = useState("");
  const [freezeClosingRate, setFreezeClosingRate] = useState(false);
  const [closingRateDay, setClosingRateDay] = useState("");
  const [graceDaysLimit, setGraceDaysLimit] = useState("5");

  useEffect(() => {
    if (!data?.data) return;
    // Evita que una revalidación de SWR en segundo plano (p.ej. al recuperar el foco
    // de la ventana tras abrir el selector de archivos) sobrescriba cambios sin guardar.
    if (edited) return;
    // La API devuelve automatic como booleano (el modelo lo castea), y este
    // formulario va como multipart por el logo, así que un booleano se
    // convertía en el texto "true"/"false". La regla `boolean` de Laravel
    // rechaza esos strings: guardar la configuración fallaba con 406 y el logo
    // no se cargaba nunca. Además los radios comparan con === 1 y === 0, así
    // que con un booleano ninguno se veía marcado.
    setSelectedOption(data?.data.automatic ? 1 : 0);
    setLogo(data?.data.logo ?? null);
    setNote(data?.data.note ?? "");
    setBaseCurrency(data?.data.base_currency ?? "USD");
    setAutoPayWithCreditBalance(Boolean(data?.data.auto_pay_with_credit_balance));
    setEmailNotifications(data?.data.email_notifications ?? {});
    setShowControlNumber(Boolean(data?.data.show_control_number));
    setNextControlNumber(String(data?.data.next_control_number ?? ""));
    setFreezeClosingRate(Boolean(data?.data.freeze_closing_rate));
    setClosingRateDay(String(data?.data.closing_rate_day ?? ""));
    setGraceDaysLimit(String(data?.data.grace_days_limit ?? 5));
    // eslint-disable-next-line
  }, [data]);

  const handleSubmitData = async (e) => {
    e.preventDefault();

    setLoading(true);

    const nativeForm = new FormData(e.target);
    const formData = new FormData();
    formData.append("_method", "PUT");
    formData.append("charge_at", nativeForm.get("charge_at") || "");
    formData.append("delay_at", nativeForm.get("delay_at") || "");
    formData.append("discount_percentage", nativeForm.get("discount_percentage") || "");
    formData.append("discount_day", nativeForm.get("discount_day") || "");
    formData.append("automatic", selectedOption);
    formData.append("auto_pay_with_credit_balance", autoPayWithCreditBalance ? 1 : 0);
    formData.append("show_control_number", showControlNumber ? 1 : 0);
    if (nextControlNumber && nextControlNumber !== String(data?.data.next_control_number ?? "")) {
      formData.append("next_control_number", nextControlNumber);
    }
    formData.append("freeze_closing_rate", freezeClosingRate ? 1 : 0);
    // Vacío es "el último día del mes".
    formData.append("closing_rate_day", closingRateDay);
    formData.append("grace_days_limit", graceDaysLimit || 5);
    Object.entries(emailNotifications).forEach(([key, enabled]) => {
      formData.append(`email_notifications[${key}]`, enabled ? 1 : 0);
    });
    formData.append("note", note || "");
    formData.append("base_currency", baseCurrency);
    if (logo instanceof File) {
      formData.append("logo", logo);
    }

    try {
      const { code } = await updateConfig(selectedCondominium, formData);
      if (code === SUCCESS_RESPONSE.OK) {
        Toast.fire({
          type: "success",
          title: `Configuración del condominio actualizada exitosamente.`,
        });
      }
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
      setEdited(false);
    }
  };

  const renderEmailOption = ({ key, label, help }) => (
    <FormGroup check key={key} className="mb-2">
      <Label for={`email_${key}`} check>
        <Input
          id={`email_${key}`}
          type="checkbox"
          checked={emailNotifications[key] !== false}
          onChange={(e) => {
            const enabled = e.target.checked;
            setEmailNotifications((current) => ({ ...current, [key]: enabled }));
            setEdited(true);
          }}
        />
        {label}
      </Label>
      <small className="text-muted d-block">{help}</small>
    </FormGroup>
  );

  const AdditionalForm = () => {
    return (
      <Container>
        <Row>
          <Col xl={12}>
            <FormGroup>
              <Label for="charge_at">
                Días de cobro
                <small className="text-muted">
                  (máximo los días 31 de cada mes)
                </small>
              </Label>
              <Input
                id="charge_at"
                name="charge_at"
                type="number"
                min={1}
                max={31}
                className={classNames({
                  "border-danger": errors.name?.message,
                })}
                onChange={(e) => {
                  if (e.target.value > 31) {
                    e.target.value = 31;
                  }
                  setEdited(true);
                }}
                required
                innerRef={register({
                  required: {
                    value: true,
                    message: "El día de cobro es requerido.",
                  },
                  pattern: {
                    value: /^[0-9]+$/i,
                    message: "Solo se aceptan números.",
                  },
                })}
                defaultValue={data?.data.charge_at}
                placeholder="Día de cobro"
              />
            </FormGroup>
          </Col>
          <Col xl={12}>
            <FormGroup>
              <Label for="delay_at">
                Días de retraso permitido{" "}
                <small className="text-muted">
                  (máximo 15 días después de la fecha de cobro)
                </small>
              </Label>
              <Input
                id="delay_at"
                name="delay_at"
                type="number"
                min={1}
                max={15}
                className={classNames({
                  "border-danger": errors.name?.message,
                })}
                onChange={(e) => {
                  if (e.target.value > 15) {
                    e.target.value = 15;
                  }
                  setEdited(true);
                }}
                required
                innerRef={register({
                  required: "El día de retraso permitido es requerido.",
                  pattern: {
                    value: /^[0-9]+$/i,
                    message: "Solo se aceptan números.",
                  },
                })}
                defaultValue={data?.data.delay_at}
                placeholder="Día de retraso permitido"
              />
            </FormGroup>
          </Col>
        </Row>
      </Container>
    );
  };

  // A diferencia de charge_at/delay_at (que solo aplican si el cobro
  // automático está activado), el descuento por pronto pago es independiente
  // de eso: se renderiza siempre, no dentro de AdditionalForm.
  const DiscountForm = () => {
    return (
      <Container>
        <Row>
          <Col xl={12}>
            <FormGroup>
              <Label for="discount_percentage">
                % de descuento por pronto pago{" "}
                <small className="text-muted">
                  (opcional; sobre el monto total de la planilla)
                </small>
              </Label>
              <Input
                id="discount_percentage"
                name="discount_percentage"
                type="number"
                min={0}
                max={100}
                step={0.01}
                onChange={(e) => {
                  if (e.target.value > 100) {
                    e.target.value = 100;
                  }
                  setEdited(true);
                }}
                innerRef={register({
                  pattern: {
                    value: /^[0-9]+([.][0-9]+)?$/i,
                    message: "Solo se aceptan números.",
                  },
                })}
                defaultValue={data?.data.discount_percentage}
                placeholder="Ej: 10"
              />
            </FormGroup>
          </Col>
          <Col xl={12}>
            <FormGroup>
              <Label for="discount_day">
                Día límite de pronto pago{" "}
                <small className="text-muted">
                  (opcional; día del mes, default para nuevos períodos)
                </small>
              </Label>
              <Input
                id="discount_day"
                name="discount_day"
                type="number"
                min={1}
                max={31}
                onChange={(e) => {
                  if (e.target.value > 31) {
                    e.target.value = 31;
                  }
                  setEdited(true);
                }}
                innerRef={register({
                  pattern: {
                    value: /^[0-9]+$/i,
                    message: "Solo se aceptan números.",
                  },
                })}
                defaultValue={data?.data.discount_day}
                placeholder="Ej: 10"
              />
            </FormGroup>
          </Col>
        </Row>
      </Container>
    );
  };

  return (
    <div className={isFluid ? "container-fluid" : "container"}>
      <Row
        style={{
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <PreloadState cond={true} loading={isLoading}>
          <ImageCropper
            initialState={logo}
            onChange={(file, loader) => {
              setLogo(file);
              setEdited(true);
              setTimeout(() => {
                loader(false);
              }, 1234);
            }}
          />
        </PreloadState>
      </Row>
      <PageHeader
        titleTag="h2"
        col={{ lg: 9 }}
        className="mb-3"
        title={`Configuración del condominio ${condominium}`}
        description="Cambia las configuraciones de tu condominio."
      >
        <SupportLink />
      </PageHeader>
      <Alert color="info" className="shadow-sm">
        <h5 className="alert-heading">INFORMACIÓN IMPORTANTE</h5>
        <p className="fs--1">
          <b>Días de cobro:</b> Días de cada mes donde el sistema creará las
          cuentas por pagar a los propietarios.
        </p>
        <p className="fs--1">
          <b>Días de retraso permitido:</b> Días de cada mes donde el sistema
          realizará penalizaciones si algún propietario no ha realizado la
          cancelación de las cuentas por pagar.
        </p>
      </Alert>
      <Alert color="warning" className="shadow-sm">
        <h5 className="alert-heading">INFORMACIÓN IMPORTANTE</h5>
        <p className="fs--1">
          {!selectedOption ? (
            <>
              Actualmente, el cobro de gastos ordinarios se está realizando de
              manera manual. Para llevar a cabo este proceso, puedes dirigirte
              al{" "}
              <Link
                to={{
                  pathname: `/dashboard/finance/${selectedCondominium}/common-costs`,
                  state: { type_id: 1, type_name: "Gastos ordinarios" },
                }}
              >
                módulo de gastos ordinarios
              </Link>
              , donde encontrarás la opción para efectuar el cobro manual de
              estos gastos.
            </>
          ) : (
            <>
              Actualmente, el cobro de gastos ordinarios se está realizando de
              manera automática. Sin embargo, si prefieres hacerlo manualmente,
              tienes la opción de hacerlo. Solo necesitas dirigirte al{" "}
              <Link
                to={{
                  pathname: `/dashboard/finance/${selectedCondominium}/common-costs`,
                  state: { type_id: 1, type_name: "Gastos ordinarios" },
                }}
              >
                módulo de gastos ordinarios
              </Link>
              , donde encontrarás la opción para efectuar el cobro manual de
              estos gastos.
            </>
            /*  "Actualmente, el cobro de gastos ordinarios se está realizando de manera automática" */
          )}
        </p>
      </Alert>
      <Card tag={Form} onSubmit={handleSubmitData}>
        <PreloadState cond={true} loading={isLoading}>
          <FalconCardHeader title="Edita la configuración" />
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label for="charge_at">
                  Cobro de gastos ordinarios automáticos:
                </Label>
                <Container>
                  <FormGroup check>
                    <Label for="automate_si" check>
                      <Input
                        id="automate_si"
                        type="checkbox"
                        disabled
                        checked={selectedOption === 1 && selectedOption}
                        onChange={() => {
                          if (selectedOption === 0) {
                            setSelectedOption(1);
                            setEdited(true);
                          }
                        }}
                      />
                      Si, realizar cobro de gastos ordinarios automáticos.
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Label for="automate_no" check>
                      <Input
                        id="automate_no"
                        type="checkbox"
                        disabled
                        checked={selectedOption === 0 && !selectedOption}
                        onChange={() => {
                          if (selectedOption === 1) {
                            setSelectedOption(0);
                            setEdited(true);
                          }
                        }}
                      />
                      No, no realizar cobro de gastos ordinarios automáticos.
                    </Label>
                  </FormGroup>
                </Container>
              </Col>
              {Boolean(selectedOption) && (
                <Col xl={12} className="mt-2">
                  <AdditionalForm />
                </Col>
              )}
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label for="discount_percentage">
                  Descuento por pronto pago de la planilla:
                </Label>
                <DiscountForm />
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label>Pago con saldo a favor:</Label>
                <Container>
                  <FormGroup check>
                    <Label for="auto_pay_with_credit_balance" check>
                      <Input
                        id="auto_pay_with_credit_balance"
                        type="checkbox"
                        checked={autoPayWithCreditBalance}
                        onChange={(e) => {
                          setAutoPayWithCreditBalance(e.target.checked);
                          setEdited(true);
                        }}
                      />
                      Al emitir la planilla, pagarla automáticamente con el
                      saldo a favor de cada propietario.
                    </Label>
                  </FormGroup>
                  <small className="text-muted">
                    Si el saldo no alcanza, se abona lo que haya, empezando por
                    la deuda más vieja. Si el propietario ya reportó un pago de
                    ese período, ese período no se toca. También se puede
                    cambiar en la ventana de emitir la planilla.
                  </small>
                </Container>
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label>Número de control:</Label>
                <Container>
                  <FormGroup check>
                    <Label for="show_control_number" check>
                      <Input
                        id="show_control_number"
                        type="checkbox"
                        checked={showControlNumber}
                        onChange={(e) => {
                          setShowControlNumber(e.target.checked);
                          setEdited(true);
                        }}
                      />
                      Mostrar el número de control en la planilla de cada
                      propietario.
                    </Label>
                  </FormGroup>
                  <small className="text-muted">
                    Cada planilla de una unidad en un período recibe el
                    siguiente número, como un talonario, aunque no se muestre.
                  </small>
                  <FormGroup className="mt-3 mb-0">
                    <Label for="next_control_number">Próximo número</Label>
                    <Input
                      id="next_control_number"
                      type="number"
                      min={(data?.data.last_control_number || 0) + 1}
                      value={nextControlNumber}
                      style={{ maxWidth: 200 }}
                      onChange={(e) => {
                        setNextControlNumber(e.target.value);
                        setEdited(true);
                      }}
                    />
                    <small className="text-muted">
                      {data?.data.last_control_number
                        ? `El último número usado es ${data.data.last_control_number}. `
                        : ""}
                      Si ya llevabas un talonario, escribe el número con el que
                      debe seguir.
                    </small>
                  </FormGroup>
                </Container>
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label for="base_currency">Moneda de trabajo</Label>
                {data?.data.currency_locked ? (
                  <>
                    <Input
                      value={
                        baseCurrency === "EUR" ? "Euro (EUR)" : "Dólar (USD)"
                      }
                      disabled
                    />
                    <small className="text-muted">
                      No se puede cambiar porque el condominio ya tiene
                      actividad financiera registrada.
                    </small>
                  </>
                ) : (
                  <>
                    <Input
                      id="base_currency"
                      type="select"
                      value={baseCurrency}
                      onChange={(e) => {
                        setBaseCurrency(e.target.value);
                        setEdited(true);
                      }}
                    >
                      <option value="USD">Dólar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                    </Input>
                    <small className="text-muted">
                      Todos los montos del condominio quedarán expresados en
                      esta moneda. Una vez que exista actividad financiera,
                      no se podrá volver a cambiar.
                    </small>
                  </>
                )}
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label>Tasa de cierre:</Label>
                <Container>
                  <FormGroup check>
                    <Label for="freeze_closing_rate" check>
                      <Input
                        id="freeze_closing_rate"
                        type="checkbox"
                        checked={freezeClosingRate}
                        onChange={(e) => {
                          setFreezeClosingRate(e.target.checked);
                          setEdited(true);
                        }}
                      />
                      Congelar tasa de cierre mensual durante periodo de pago.
                    </Label>
                  </FormGroup>
                  <small className="text-muted">
                    Los pagos de una planilla hechos en los primeros días del
                    mes siguiente se convierten con la tasa del cierre del mes
                    facturado. Después de esos días se usa la tasa del día del
                    pago.
                  </small>
                  {freezeClosingRate && (
                    <Row className="mt-3">
                      <Col md={6}>
                        <FormGroup className="mb-0">
                          <Label for="closing_rate_day">
                            Día de corte para la tasa de referencia
                          </Label>
                          <Input
                            id="closing_rate_day"
                            type="select"
                            value={closingRateDay}
                            onChange={(e) => {
                              setClosingRateDay(e.target.value);
                              setEdited(true);
                            }}
                          >
                            <option value="">Último día del mes facturado</option>
                            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
                              <option key={day} value={day}>
                                Día {day}
                              </option>
                            ))}
                          </Input>
                          <small className="text-muted">
                            Si el mes no tiene ese día se usa el último. Si ese
                            día no hay tasa publicada (fin de semana o feriado),
                            se usa la siguiente publicada.
                          </small>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="mb-0">
                          <Label for="grace_days_limit">
                            Días de gracia de tasa congelada
                          </Label>
                          <Input
                            id="grace_days_limit"
                            type="number"
                            min={1}
                            max={28}
                            value={graceDaysLimit}
                            style={{ maxWidth: 200 }}
                            onChange={(e) => {
                              setGraceDaysLimit(e.target.value);
                              setEdited(true);
                            }}
                          />
                          <small className="text-muted">
                            Hasta el día {graceDaysLimit || 5} del mes
                            siguiente, inclusive.
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>
                  )}
                </Container>
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row form className="mb-2">
              <Col xl={12}>
                <Label for="note">
                  {logo === ""
                    ? "Escribe una nota para el reporte:"
                    : "Nota del reporte:"}
                </Label>
                <Container>
                  <FormGroup className="mb-0">
                    <Input
                      id="note"
                      defaultValue={note}
                      onChange={(e) => {
                        setNote(e.target.value);
                        !edited && setEdited(true);
                        e.target.value === "" && setEdited(false);
                      }}
                      type="textarea"
                      className="w-100"
                      placeholder="Sistema de administracion web para condominios"
                    />
                  </FormGroup>
                  <small className="d-block text-600 fs--1 mt-1">
                    Cada planilla guarda la nota que tenía al emitirse. Si la
                    cambias aquí, las planillas ya emitidas no cambian.
                  </small>
                </Container>
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardBody>
            <Row>
              <Col xl={12}>
                <Label>Correos automáticos:</Label>
                <p className="fs--1 text-muted mb-2">
                  Elige qué correos envía Homelan por su cuenta. Los que mandas
                  con un botón, como los avisos de cobro, salen siempre.
                </p>
                <Container>
                  <h6 className="mt-2">A los propietarios</h6>
                  {OWNER_EMAILS.filter(
                    (email) => !email.onlyWithChargeDay || data?.data.charge_at
                  ).map(renderEmailOption)}
                  <h6 className="mt-3">A ti</h6>
                  {ADMIN_EMAILS.map(renderEmailOption)}
                </Container>
              </Col>
            </Row>
          </CardBody>
          <hr style={{ margin: "0px" }} />
          <CardFooter>
            <Row>
              <Col className="d-flex justify-content-end align-items-center">
                <ButtonIcon
                  color="falcon-default"
                  className="px-5 mr-2"
                  icon={faUndo}
                  onClick={() => history.goBack()}
                  iconAlign="right"
                  transform="down-1 shrink-4"
                >
                  Volver
                </ButtonIcon>
                <ButtonIcon
                  data-testid="submit-button"
                  color="primary"
                  className="px-5 ml-2"
                  type="submit"
                  icon="save"
                  iconAlign="right"
                  disabled={loading || !edited}
                  transform="down-1 shrink-4"
                >
                  {loading ? (
                    <>
                      <Spinner size={"sm"} color="light" className="mr-2" />
                      Un momento
                    </>
                  ) : (
                    "Guardar"
                  )}
                </ButtonIcon>
              </Col>
            </Row>
          </CardFooter>
        </PreloadState>
      </Card>
      {/* HO-HU-36: fuera del Card/Form de arriba a proposito -- se guarda solo
          al tocarlo y no participa del `edited`/handleSubmitData. */}
      <OnboardingVisibilityCard />
    </div>
  );
};

export default withRedirect(Config);
